import { unzipSync, zipSync, strFromU8, strToU8 } from "fflate";
import type { EntityData } from "@/types/entity";
import type { ContactData } from "@/types/contact";
import { NOTIFICATION_SERVICE_ROWS } from "@/lib/file-extraction/constants";

const TEMPLATE_PATH = "/assets/instrumentul-nis2ro-v-2-2.xlsx";
const SS_PATH = "xl/sharedStrings.xml";
const SHEET_ENTITY_PATH = "xl/worksheets/sheet3.xml"; // "Date entitate"
const SHEET_EVAL_PATH = "xl/worksheets/sheet4.xml"; // "Evaluare entitate"

// ── XML helpers ──────────────────────────────────────────────────────────────

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function unescapeXml(s: string): string {
  return s
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");
}

// ── Shared-string pool ───────────────────────────────────────────────────────

class StringPool {
  private readonly byValue = new Map<string, number>();
  private readonly additions: string[] = [];
  private readonly originalCount: number;

  constructor(ssXml: string) {
    const matches = [...ssXml.matchAll(/<si>([\s\S]*?)<\/si>/g)];
    this.originalCount = matches.length;
    matches.forEach((m, i) => {
      // Concatenate all <t> runs to handle both plain and rich-text entries
      const tRuns = [...m[1].matchAll(/<t[^>]*>([^<]*)<\/t>/g)];
      if (tRuns.length > 0) {
        const t = tRuns.map((r) => unescapeXml(r[1])).join("");
        this.byValue.set(t, i);
      }
    });
  }

  getOrAdd(value: string): number {
    const existing = this.byValue.get(value);
    if (existing !== undefined) return existing;
    const newIdx = this.additions.indexOf(value);
    if (newIdx >= 0) return this.originalCount + newIdx;
    this.additions.push(value);
    return this.originalCount + this.additions.length - 1;
  }

  patchXml(ssXml: string): string {
    if (this.additions.length === 0) return ssXml;
    const extra = this.additions
      .map((s) => `<si><t>${escapeXml(s)}</t></si>`)
      .join("");
    const newCount = this.originalCount + this.additions.length;
    let result = ssXml.replace("</sst>", `${extra}</sst>`);
    result = result.replace(/\bcount="[^"]*"/, `count="${newCount}"`);
    result = result.replace(
      /\buniqueCount="[^"]*"/,
      `uniqueCount="${newCount}"`
    );
    return result;
  }
}

// ── Cell writer ──────────────────────────────────────────────────────────────

function setCellStr(xml: string, addr: string, strIdx: number): string {
  // Matches both self-closing and open/close variants of this cell address.
  // Preserves the existing s="N" style attribute, replaces everything else.
  const re = new RegExp(`<c r="${addr}"([^>]*?)(?:\\s*/>|\\s*>[\\s\\S]*?</c>)`);
  const result = xml.replace(re, (_, attrs: string) => {
    const sAttr = (attrs.match(/\s*s="([^"]*)"/) ?? [])[1];
    const s = sAttr != null ? ` s="${sAttr}"` : "";
    return `<c r="${addr}"${s} t="s"><v>${strIdx}</v></c>`;
  });
  if (result === xml) {
    console.error(
      `[XLSX] Cell ${addr} not found in sheet — template may have changed`
    );
  }
  return result;
}

function setCellNum(xml: string, addr: string, value: number): string {
  const re = new RegExp(`<c r="${addr}"([^>]*?)(?:\\s*/>|\\s*>[\\s\\S]*?</c>)`);
  const result = xml.replace(re, (_, attrs: string) => {
    const sAttr = (attrs.match(/\s*s="([^"]*)"/) ?? [])[1];
    const s = sAttr != null ? ` s="${sAttr}"` : "";
    return `<c r="${addr}"${s}><v>${value}</v></c>`;
  });
  if (result === xml) {
    console.error(
      `[XLSX] Cell ${addr} not found in sheet — template may have changed`
    );
  }
  return result;
}

// ── Main export ──────────────────────────────────────────────────────────────

export async function generateXlsxReport(
  entityData: EntityData,
  contactData: ContactData
): Promise<void> {
  const response = await fetch(TEMPLATE_PATH);
  if (!response.ok)
    throw new Error(`Failed to fetch XLSX template: ${response.statusText}`);

  const files = unzipSync(new Uint8Array(await response.arrayBuffer()));
  const pool = new StringPool(strFromU8(files[SS_PATH]));

  // Helper: write a string cell (skips if value is null/undefined/"")
  const ws = (
    xml: string,
    addr: string,
    value: string | null | undefined
  ): string => (value ? setCellStr(xml, addr, pool.getOrAdd(value)) : xml);

  // Helper: write a number cell (skips if value is null/undefined)
  const wn = (
    xml: string,
    addr: string,
    value: number | null | undefined
  ): string => (value != null ? setCellNum(xml, addr, value) : xml);

  const da = (v: boolean | null | undefined) =>
    v === true ? "DA" : v === false ? "NU" : undefined;

  const anaf = entityData.anaf_raw_data?.adresa_sediu_social;
  const relatie = entityData.relatie_stat_roman;
  const serviciiUE = entityData.situatie_servicii_ue;
  const art9 = entityData.impact_analysis?.result;
  const rl = contactData.reprezentantLegal;
  const rs = contactData.responsabilSecuritate;
  const co = contactData.contactOperational;

  // ── Sheet 3: "Date entitate" ────────────────────────────────────────────
  let e = strFromU8(files[SHEET_ENTITY_PATH]);

  e = ws(e, "D12", entityData.nume_entitate);
  e = ws(e, "D13", entityData.cui);
  e = ws(e, "D14", entityData.cif);
  e = ws(e, "D17", anaf?.sdenumire_Judet);
  e = ws(e, "D18", anaf?.sdenumire_Localitate);
  e = ws(e, "D19", anaf?.sdetalii_Adresa ?? undefined);
  e = ws(e, "D20", anaf?.sdenumire_Strada);
  e = ws(e, "D21", anaf?.snumar_Strada);
  e = ws(e, "D26", anaf?.scod_Postal);
  e = ws(e, "D27", entityData.email);
  e = ws(e, "D28", entityData.website);
  e = ws(e, "D29", entityData.telefon);
  e = ws(e, "D30", entityData.anaf_raw_data?.date_generale?.fax || undefined);
  e = ws(e, "D32", entityData.caen_principal?.cod);
  e = ws(e, "D33", entityData.caen_secundar?.join(", ") || undefined);

  // Security person
  const hasRS = !!rs.nume?.trim();
  e = ws(e, "C37", hasRS ? "DA" : undefined);
  e = ws(e, "D38", rs.nume || undefined);
  e = ws(e, "D39", rs.prenume || undefined);
  e = ws(e, "D40", rs.functie || undefined);
  e = ws(e, "D41", rs.email || undefined);
  e = ws(e, "D42", rs.telefon || undefined);

  // Contact operational
  const sameAsRS =
    !!co.email &&
    co.email === rs.email &&
    co.nume === rs.nume &&
    co.prenume === rs.prenume;
  e = ws(e, "C45", sameAsRS ? "DA" : hasRS ? "NU" : undefined);
  if (!sameAsRS) {
    e = ws(e, "D46", co.nume || undefined);
    e = ws(e, "D47", co.prenume || undefined);
    e = ws(e, "D48", co.functie || undefined);
    e = ws(e, "D50", co.telefon || undefined);
    e = ws(e, "D52", co.email || undefined);
  }

  const ipList = contactData.adreseIP?.filter(Boolean).join(", ");
  e = ws(e, "D54", ipList || undefined);

  // Legal representative (bottom of sheet)
  e = ws(e, "D81", rl.nume || undefined);
  e = ws(e, "D82", rl.prenume || undefined);
  e = ws(e, "D83", rl.functie || undefined);

  files[SHEET_ENTITY_PATH] = strToU8(e);

  // ── Sheet 4: "Evaluare entitate" ────────────────────────────────────────
  let v = strFromU8(files[SHEET_EVAL_PATH]);

  v = wn(v, "D17", entityData.nr_salariati ?? undefined);
  v = wn(v, "D18", entityData.cifra_afaceri ?? undefined);
  v = wn(v, "D19", entityData.active ?? undefined);

  // Dimensiune — template uses "Mică"/"Mijlocie"/"Mare"
  const dimMap: Record<string, string> = {
    MICĂ: "Mică",
    MIJLOCIE: "Mijlocie",
    MARE: "Mare",
  };
  const dim = entityData.dimensiune ? dimMap[entityData.dimensiune] : undefined;
  v = ws(v, "D21", dim);

  // Relație stat roman
  v = ws(v, "D150", da(relatie?.stabilita_in_romania));
  v = ws(v, "D151", da(relatie?.sediu_principal_in_romania));

  // Servicii UE
  v = ws(v, "D159", da(serviciiUE?.furnizeaza_servicii_romania));
  v = ws(v, "D160", da(serviciiUE?.furnizeaza_servicii_ue));
  v = ws(v, "D161", serviciiUE?.state_membre_ue || undefined);

  // Article 9
  v = ws(v, "D169", da(art9?.article9a));
  v = ws(v, "D170", art9?.article9b ?? undefined);
  v = ws(v, "D171", art9?.article9c ?? undefined);
  v = ws(v, "D172", da(art9?.article9d));

  // Classification
  const classMap: Partial<Record<string, string>> = {
    ESSENTIAL: "Entitate esențială",
    IMPORTANT: "Entitate importantă",
  };
  const classLabel =
    classMap[entityData.nis_classification?.classificationV2 ?? ""];
  v = ws(v, "D176", classLabel);

  // Selected services — write "DA" for each selected row
  const selectedIds = new Set(
    (entityData.selected_services ?? []).map((s) => s.entity_id?.toString())
  );
  for (const row of NOTIFICATION_SERVICE_ROWS) {
    if (selectedIds.has(row.entityId)) {
      v = ws(v, `D${row.row}`, "DA");
    }
  }

  files[SHEET_EVAL_PATH] = strToU8(v);

  // ── Patch sharedStrings if new strings were added ───────────────────────
  const patchedSS = pool.patchXml(strFromU8(files[SS_PATH]));
  files[SS_PATH] = strToU8(patchedSS);

  // ── Rezip and download ───────────────────────────────────────────────────
  const zipped = zipSync(files);
  const blob = new Blob([zipped.buffer as ArrayBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `Instrumentul_NIS2_${entityData.cui || "Entitate"}.xlsx`;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}
